import { useEffect, useState } from "react";
import { Autocomplete, IconButton, TextField } from "@mui/material";
import { AddCircle, DeleteOutline } from "@mui/icons-material";
import { getApplicableExtraCharges } from "../../master-data/extra-charges/apis";
import { CALCULATION_TYPE_OPTIONS } from "../../master-data/extra-charges/utils";
import MuiTable from "../../../../common-utilities/core/mui-table";
import { UOM_LIST } from "../helper";
import "./extra-charges-section.scss";

const READ_ONLY_COLUMNS = [
  {
    label: "Charge",
    id: "chargeName",
    render: (row) =>
      row.chargeCode ? `${row.chargeCode} - ${row.chargeName || ""}` : row.chargeName || "-",
  },
  {
    label: "HSN Code",
    id: "hsnCode",
    render: (row) => row.hsnCode || "-",
  },
  {
    label: "Calculation Type",
    id: "calculationType",
    render: (row) =>
      CALCULATION_TYPE_OPTIONS.find((op) => op.value === row.calculationType)?.label || "-",
  },
  {
    label: "Value",
    id: "value",
    render: (row) => (row.value === "" || row.value === null || row.value === undefined ? "-" : row.value),
  },
  {
    label: "Quantity",
    id: "quantity",
    render: (row) => (row.quantity === "" || row.quantity === null || row.quantity === undefined ? "-" : row.quantity),
  },
  {
    label: "UOM",
    id: "uom",
    render: (row) => UOM_LIST.find((u) => u.value === row.uom)?.label || "-",
  },
  {
    label: "Tax Rate",
    id: "taxRate",
    render: (row) => (row.taxable ? `${row.taxRate}%` : "Not Taxable"),
  },
  {
    label: "Charge Amount",
    id: "chargeAmount",
    render: (row) => row.chargeAmount || 0,
  },
  {
    label: "Tax Amount",
    id: "taxAmount",
    render: (row) => row.taxAmount || 0,
  },
  {
    label: "Total",
    id: "totalChargeAmount",
    render: (row) => row.totalChargeAmount || 0,
  },
  {
    label: "Remarks",
    id: "remarks",
    render: (row) => row.remarks || "-",
  },
];

export const EMPTY_CHARGE_ROW = {
  extraChargeId: null,
  chargeCode: "",
  chargeName: "",
  hsnCode: "",
  calculationType: null,
  valueMode: null,
  value: "",
  quantity: "",
  uom: null,
  taxable: false,
  taxRate: 0,
  taxAmount: 0,
  chargeAmount: 0,
  totalChargeAmount: 0,
  affectsPOTotal: false,
  affectsBudget: false,
  remarks: "",
};

const round2 = (num) =>
  Math.round((Number(num || 0) + Number.EPSILON) * 100) / 100;

export const isIncompleteChargeRow = (row) =>
  !row?.extraChargeId ||
  !row?.calculationType ||
  !row?.valueMode ||
  row?.value === "" ||
  row?.value === null ||
  row?.value === undefined ||
  (row?.calculationType === "FIXED_AMOUNT" &&
    (!hasEnteredValue(row?.quantity) || !row?.uom));

const isBlank = (value) => value === "" || value === null || value === undefined;

export const isEmptyChargeRow = (row) =>
  isBlank(row?.extraChargeId) &&
  isBlank(row?.value) &&
  isBlank(row?.quantity) &&
  isBlank(row?.uom) &&
  isBlank(typeof row?.remarks === "string" ? row.remarks.trim() : row?.remarks);

export const stripEmptyChargeRows = (charges = []) =>
  (charges || []).filter((row) => !isEmptyChargeRow(row));

export const hasIncompleteChargeRow = (charges = []) =>
  stripEmptyChargeRows(charges).some(isIncompleteChargeRow);

const hasEnteredValue = (value) =>
  value !== "" && value !== null && value !== undefined && !Number.isNaN(Number(value));

const computeAmounts = (row, poSubtotalBeforeTax) => {
  const value = Number(row.value) || 0;

  let chargeAmount = 0;

  if (row.calculationType === "FIXED_AMOUNT") {
    // Value is the unit rate; quantity is mandatory for this calculation type.
    chargeAmount = hasEnteredValue(row.quantity) ? Number(row.quantity) * value : 0;
  } else if (row.calculationType === "PERCENTAGE") {
    chargeAmount = ((Number(poSubtotalBeforeTax) || 0) * value) / 100;
  }

  chargeAmount = round2(chargeAmount);

  const taxAmount = row.taxable
    ? round2((chargeAmount * (Number(row.taxRate) || 0)) / 100)
    : 0;

  const totalChargeAmount = round2(chargeAmount + taxAmount);

  return { chargeAmount, taxAmount, totalChargeAmount };
};

const ExtraChargesSection = ({
  charges = [],
  onChangeCharges,
  poSubtotalBeforeTax = 0,
  readOnly = false,
}) => {
  const [activeCharges, setActiveCharges] = useState([]);
  const [chargesLoaded, setChargesLoaded] = useState(false);

  useEffect(() => {
    if (readOnly) return;
    getApplicableExtraCharges((list) => {
      setActiveCharges(list);
      setChargesLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (readOnly || !chargesLoaded || activeCharges.length) return;
    if (charges.some(isEmptyChargeRow)) {
      onChangeCharges(stripEmptyChargeRows(charges));
    }
  }, [chargesLoaded, activeCharges, charges]);

  const getRowOptions = (row) => {
    if (
      isBlank(row.extraChargeId) ||
      activeCharges.some((c) => String(c.id) === String(row.extraChargeId))
    ) {
      return activeCharges;
    }
    return [...activeCharges, { ...row, id: row.extraChargeId }];
  };

  useEffect(() => {
    if (readOnly || !charges.length) return;

    let changed = false;

    const next = charges.map((row) => {
      const { chargeAmount, taxAmount, totalChargeAmount } = computeAmounts(
        row,
        poSubtotalBeforeTax
      );

      if (
        chargeAmount === row.chargeAmount &&
        taxAmount === row.taxAmount &&
        totalChargeAmount === row.totalChargeAmount
      ) {
        return row;
      }

      changed = true;

      return {
        ...row,
        chargeAmount,
        taxAmount,
        totalChargeAmount,
      };
    });

    if (changed) {
      onChangeCharges(next);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poSubtotalBeforeTax]);

  const onAddCharge = () => {
    onChangeCharges([...charges, { ...EMPTY_CHARGE_ROW }]);
  };

  const onDeleteCharge = (index) => {
    const next = [...charges];
    next.splice(index, 1);
    onChangeCharges(next);
  };

  const onChargeSelect = (index, selected) => {
    const next = [...charges];

    if (!selected) {
      next[index] = { ...EMPTY_CHARGE_ROW };
      onChangeCharges(next);
      return;
    }

    const {
      id,
      chargeCode,
      chargeName,
      hsnCode,
      calculationType,
      valueMode,
      defaultValue,
      taxable,
      taxRate,
      affectsPOTotal,
      affectsBudget,
    } = selected;

    const patched = {
      extraChargeId: id,
      chargeCode,
      chargeName,
      hsnCode,
      calculationType,
      valueMode,
      value: valueMode === "CONFIGURED_VALUE" ? Number(defaultValue) || 0 : "",
      quantity: "",
      uom: null,
      taxable: !!taxable,
      taxRate: taxable ? Number(taxRate) || 0 : 0,
      affectsPOTotal: !!affectsPOTotal,
      affectsBudget: !!affectsBudget,
      remarks: "",
    };

    const { chargeAmount, taxAmount, totalChargeAmount } = computeAmounts(
      patched,
      poSubtotalBeforeTax
    );

    next[index] = {
      ...patched,
      chargeAmount,
      taxAmount,
      totalChargeAmount,
    };

    onChangeCharges(next);
  };

  const onValueChange = (index, rawValue) => {
    const next = [...charges];

    const patchedRow = {
      ...next[index],
      value: rawValue,
    };

    const { chargeAmount, taxAmount, totalChargeAmount } = computeAmounts(
      patchedRow,
      poSubtotalBeforeTax
    );

    next[index] = {
      ...patchedRow,
      chargeAmount,
      taxAmount,
      totalChargeAmount,
    };

    onChangeCharges(next);
  };

  const onQuantityChange = (index, rawValue) => {
    const next = [...charges];

    const patchedRow = {
      ...next[index],
      quantity: rawValue,
    };

    const { chargeAmount, taxAmount, totalChargeAmount } = computeAmounts(
      patchedRow,
      poSubtotalBeforeTax
    );

    next[index] = {
      ...patchedRow,
      chargeAmount,
      taxAmount,
      totalChargeAmount,
    };

    onChangeCharges(next);
  };

  const onUomChange = (index, uomValue) => {
    const next = [...charges];
    next[index] = { ...next[index], uom: uomValue };
    onChangeCharges(next);
  };

  const onRemarksChange = (index, rawValue) => {
    const next = [...charges];
    next[index] = { ...next[index], remarks: rawValue };
    onChangeCharges(next);
  };

  if (readOnly && !charges.length) {
    return null;
  }

  if (readOnly) {
    return (
      <div className="po-item-card finance-v2-extra-charges-card">
        <div className="po-item-header finance-v2-extra-charges-header">
          <span className="po-item-title">Extra Charges</span>
        </div>
        <MuiTable columnsList={READ_ONLY_COLUMNS} dataList={charges} />
      </div>
    );
  }

  const noChargesForDepartment = !chargesLoaded || !activeCharges.length;

  if (noChargesForDepartment && !stripEmptyChargeRows(charges).length) {
    return null;
  }

  return (
    <div className="po-item-card finance-v2-extra-charges-card">
      <div className="po-item-header finance-v2-extra-charges-header">
        <span className="po-item-title">Extra Charges</span>
        {!noChargesForDepartment && (
          <IconButton onClick={onAddCharge}>
            <AddCircle color="primary" />
          </IconButton>
        )}
      </div>

      {charges.map((row, index) => {
        const rowOptions = getRowOptions(row);
        return (
        <div className="po-item-row finance-v2-extra-charge-row" key={index}>
          <Autocomplete
            options={rowOptions}
            value={
              rowOptions.find(
                (c) => String(c.id) === String(row.extraChargeId)
              ) || null
            }
            getOptionLabel={(op) =>
              `${op.chargeCode || ""} - ${op.chargeName || ""}`
            }
            onChange={(e, val) => onChargeSelect(index, val)}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Charge" />
            )}
          />

          <TextField
            size="small"
            label="HSN Code"
            disabled
            value={row.hsnCode || ""}
          />

          <TextField
            size="small"
            label="Calculation Type"
            disabled
            value={
              CALCULATION_TYPE_OPTIONS.find(
                (op) => op.value === row.calculationType
              )?.label || ""
            }
          />

          <TextField
            size="small"
            type="number"
            label="Value"
            disabled={row.valueMode === "CONFIGURED_VALUE"}
            value={row.value}
            onChange={(e) => onValueChange(index, e.target.value)}
          />

          <TextField
            size="small"
            type="number"
            label="Quantity"
            required={row.calculationType === "FIXED_AMOUNT"}
            value={row.quantity}
            onChange={(e) => onQuantityChange(index, e.target.value)}
          />

          <Autocomplete
            size="small"
            options={UOM_LIST}
            value={UOM_LIST.find((u) => u.value === row.uom) || null}
            getOptionLabel={(op) => op.label || ""}
            onChange={(e, val) => onUomChange(index, val?.value || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="UOM"
                required={row.calculationType === "FIXED_AMOUNT"}
              />
            )}
          />

          <TextField
            size="small"
            label="Tax Rate"
            disabled
            value={row.taxable ? `${row.taxRate}%` : "Not Taxable"}
          />

          <TextField
            size="small"
            label="Charge Amount"
            disabled
            value={row.chargeAmount || 0}
          />

          <TextField
            size="small"
            label="Tax Amount"
            disabled
            value={row.taxAmount || 0}
          />

          <TextField
            size="small"
            label="Total Charge"
            disabled
            value={row.totalChargeAmount || 0}
          />

          <TextField
            size="small"
            label="Remarks"
            value={row.remarks || ""}
            onChange={(e) => onRemarksChange(index, e.target.value)}
          />

          <IconButton onClick={() => onDeleteCharge(index)}>
            <DeleteOutline color="primary" />
          </IconButton>
        </div>
        );
      })}
    </div>
  );
};

export default ExtraChargesSection;